import { useId } from 'react';
import type { SortPreset } from '@/domain';
import { strings } from '@/shared/strings';
import filters from './FilterBar.module.css';

/**
 * The order control, in the filter bar above the table. Section 3 of the
 * request of 13 September 2026.
 *
 * A select rather than a row of buttons: there are up to five of these and the
 * bar is already four controls wide on a phone. It wears the filter bar's own
 * field styles so it reads as one more control on the bar rather than as
 * something bolted above it.
 *
 * The state behind it is `useTableOrder`, which is also the table's column sort:
 * the two cannot disagree because there is only one of them.
 */
export function SortSelect<Row>({
  presets,
  value,
  onChange,
}: {
  presets: readonly SortPreset<Row>[];
  value: string;
  onChange: (key: string) => void;
}) {
  const id = useId();

  return (
    <>
      <label className={filters.l} htmlFor={id}>
        {strings.table.order}
      </label>
      <select
        id={id}
        className={filters.select}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        {/* Only while a column is driving, so the control never names an order
            the table is not in. Picking a preset takes it away again. */}
        {value === '' && <option value="">{strings.table.byColumn}</option>}
        {presets.map((preset) => (
          <option key={preset.key} value={preset.key}>
            {preset.label}
          </option>
        ))}
      </select>
    </>
  );
}
